import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { AutoGrowTextarea } from '@/components/ui/autoGrowTextarea';
import { SearchableSelect } from '@/components/ui/searchableSelect';
import { Button } from '@/components/ui/button';
import { MAXIMUM_DEVICE_SETTING_SETS } from '@/constants/sessionNoteDeviceSettings';
import { DeviceSettingField } from './DeviceSettingField';
import type { EditorTreatment } from './types';

const OUTCOME_OPTIONS = ['done', 'skipped', 'refused'];

interface TreatmentRowProps {
  treatment: EditorTreatment;
  bodyAreaOptions: { value: string; label: string }[];
  isDisabled: boolean;
  onChange: (treatment: EditorTreatment) => void;
  onRemove: () => void;
}

export function TreatmentRow({
  treatment,
  bodyAreaOptions,
  isDisabled,
  onChange,
  onRemove,
}: TreatmentRowProps) {
  const { t } = useTranslation('patients');

  function updateField(changes: Partial<EditorTreatment>) {
    onChange({ ...treatment, ...changes });
  }

  function updateDeviceSetting(setIndex: number, fieldId: string, value: string) {
    onChange({
      ...treatment,
      deviceSettingSets: treatment.deviceSettingSets.map(function updateSet(settingSet, index) {
        return index === setIndex ? { ...settingSet, [fieldId]: value } : settingSet;
      }),
    });
  }

  function addDeviceSettingSet() {
    onChange({ ...treatment, deviceSettingSets: [...treatment.deviceSettingSets, {}] });
  }

  function removeDeviceSettingSet(setIndex: number) {
    onChange({
      ...treatment,
      deviceSettingSets: treatment.deviceSettingSets.filter(function keepOthers(_set, index) {
        return index !== setIndex;
      }),
    });
  }

  const deviceFields = treatment.device === null ? [] : treatment.device.settingFields;
  const hasSeveralSets = treatment.deviceSettingSets.length > 1;
  const canAddSet = treatment.deviceSettingSets.length < MAXIMUM_DEVICE_SETTING_SETS;

  return (
    <div className="border border-zooborder rounded-2xl p-4 bg-zoooffwhite">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-bold text-zooindigo">{treatment.treatmentName}</p>
          {treatment.contraindication === null ? null : (
            <p className="mt-1 inline-flex items-start gap-1 text-xs font-semibold text-zoowarningtext bg-zoowarningbg rounded-lg px-2 py-1">
              <Icon name="AlertCircle" />
              {t('sessionNote.contraindication')}: {treatment.contraindication}
            </p>
          )}
        </div>
        <Button
          variant="destructiveGhost"
          size="icon"
          onClick={onRemove}
          disabled={isDisabled}
          aria-label={t('sessionNote.removeTreatment')}
          className="shrink-0"
        >
          <Icon name="Trash" />
        </Button>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 mt-3">
        <label className="text-xs font-semibold text-zoomuted">
          {t('sessionNote.outcomeLabel')}
          <SearchableSelect
            ariaLabel={t('sessionNote.outcomeLabel')}
            value={treatment.outcome}
            isDisabled={isDisabled}
            options={OUTCOME_OPTIONS.map(function toOption(outcome) {
              return {
                value: outcome,
                label: t(
                  'sessionNote.outcome' + outcome.charAt(0).toUpperCase() + outcome.slice(1)
                ),
              };
            })}
            onChange={function selectOutcome(nextValue) {
              updateField({ outcome: nextValue });
            }}
          />
        </label>
        <label className="text-xs font-semibold text-zoomuted">
          {t('sessionNote.bodyArea')}
          <SearchableSelect
            ariaLabel={t('sessionNote.bodyArea')}
            value={treatment.bodyAreaId}
            isDisabled={isDisabled}
            options={[{ value: '', label: t('sessionNote.noBodyArea') }, ...bodyAreaOptions]}
            onChange={function selectBodyArea(nextValue) {
              updateField({ bodyAreaId: nextValue });
            }}
          />
        </label>
      </div>

      <label className="block text-xs font-semibold text-zoomuted mt-3">
        {t('sessionNote.treatmentDescription')}
        <AutoGrowTextarea
          value={treatment.response}
          disabled={isDisabled}
          rows={2}
          placeholder={t('sessionNote.treatmentDescriptionPlaceholder')}
          onChange={function changeResponse(event) {
            updateField({ response: event.target.value });
          }}
          className="mt-1 w-full rounded-xl border border-zooborder bg-zoowhite px-3 py-2 text-sm text-zooindigo"
        />
      </label>

      {deviceFields.length === 0 ? null : (
        <div className="mt-3">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold uppercase tracking-wider text-zoomuted">
              {t('sessionNote.deviceSettings')}
            </p>
            {treatment.recalledFromLast === true ? (
              <span className="text-[11px] font-semibold text-zooprimary inline-flex items-center gap-1">
                <Icon name="Clock" />
                {t('sessionNote.recallFromLast')}
              </span>
            ) : null}
          </div>
          {treatment.deviceSettingSets.map(function renderSet(settingSet, setIndex) {
            const setNumber = setIndex + 1;
            return (
              <div
                key={setNumber}
                className={
                  hasSeveralSets ? 'mt-2 border-t border-zooborder pt-2 first:border-t-0' : ''
                }
              >
                {hasSeveralSets ? (
                  <div className="flex items-center justify-between">
                    <p className="text-xs font-semibold text-zooindigo">
                      {t('sessionNote.programSetTitle', { number: setNumber })}
                    </p>
                    <Button
                      variant="destructiveGhost"
                      size="chip"
                      onClick={function removeSet() {
                        removeDeviceSettingSet(setIndex);
                      }}
                      disabled={isDisabled}
                      aria-label={t('sessionNote.removeProgramSet', { number: setNumber })}
                    >
                      <Icon name="X" />
                    </Button>
                  </div>
                ) : null}
                <div className="grid gap-3 sm:grid-cols-2 mt-2">
                  {deviceFields.map(function renderField(field) {
                    if (field.id === null) {
                      return null;
                    }
                    const fieldId = field.id;
                    const settingValue = settingSet[fieldId];
                    return (
                      <DeviceSettingField
                        key={fieldId}
                        field={field}
                        value={settingValue === undefined ? '' : settingValue}
                        isDisabled={isDisabled}
                        onChange={function changeSetting(nextValue) {
                          updateDeviceSetting(setIndex, fieldId, nextValue);
                        }}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
          {canAddSet ? (
            <Button
              variant="outline"
              size="chip"
              className="mt-3 inline-flex items-center gap-1"
              onClick={addDeviceSettingSet}
              disabled={isDisabled}
            >
              <Icon name="Plus" /> {t('sessionNote.addProgramSet')}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}
